/**
 * Popup newsletter: si apre dopo un ritardo o a una certa percentuale di scorrimento, una sola volta per visitatore.
 * Usa <dialog> (focus trap, Esc e aria-modal nativi). Il codice sconto compare dopo l'iscrizione.
 * localStorage e sempre dentro try/catch: se non disponibile il popup funziona comunque, senza memoria.
 */

const STORAGE_KEY = 'gdm_newsletter_popup';
const DAY_MS = 24 * 60 * 60 * 1000;
const ALWAYS_EXCLUDED_PREFIXES = ['/cart', '/checkout', '/checkouts', '/account', '/policies', '/password'];

function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    /* memoria non disponibile: si ignora */
  }
}

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.formStep = this.querySelector('[data-popup-form-step]');
    this.successStep = this.querySelector('[data-popup-success-step]');
    this.form = this.querySelector('form');
    this.errorMessage = this.querySelector('[data-popup-error]');
    this.copyButton = this.querySelector('[data-popup-copy]');
    this.status = this.querySelector('[data-popup-status]');
    this.opened = false;

    this.querySelectorAll('[data-popup-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', () => this.rememberClosed());
    this.dialog.addEventListener('close', () => this.rememberClosed());
    this.form?.addEventListener('submit', (event) => this.submit(event));
    this.copyButton?.addEventListener('click', () => this.copyCode());

    // Design mode: nessun comportamento automatico, il popup si apre solo dall'editor.
    if (window.Shopify?.designMode) return;

    if (this.querySelector('[data-popup-posted]')) {
      // Iscrizione appena inviata (pagina ricaricata dal form senza JavaScript)
      this.showSuccess();
      this.open(true);
      return;
    }

    if (this.dataset.enabled !== 'true' || !this.isAllowed()) return;
    this.scheduleOpen();
  }

  isAllowed() {
    const path = window.location.pathname.toLowerCase();
    if (ALWAYS_EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix + '/'))) return false;
    if (/^\/[a-z]{2}(-[a-z]{2})?\/(cart|checkouts?|account|policies)(\/|$)/.test(path)) return false;

    const pageType = this.dataset.pageType || '';
    if (pageType === 'cart' || pageType === 'policy' || pageType === 'password' || pageType.startsWith('customers/')) return false;

    const excluded = (this.dataset.excluded || '').split(/\s+/).filter(Boolean);
    if (excluded.some((part) => path.includes(part.toLowerCase()))) return false;

    const state = readState();
    if (state?.status === 'subscribed') return false;
    if (state?.status === 'closed') {
      const days = Number(this.dataset.reopenDays) || 30;
      if (Date.now() - Number(state.at) < days * DAY_MS) return false;
    }
    return true;
  }

  scheduleOpen() {
    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    const scrollPercent = Math.max(0, Number(this.dataset.scroll) || 0);
    let timer = null;

    const trigger = () => {
      cleanup();
      this.openWhenCookieBannerGone();
    };
    const onScroll = () => {
      const scroller = document.querySelector('.page-wrapper');
      const container = scroller && scroller.scrollHeight > scroller.clientHeight ? scroller : document.scrollingElement;
      const max = container.scrollHeight - container.clientHeight;
      if (max > 0 && (container.scrollTop / max) * 100 >= scrollPercent) trigger();
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll, true);
    };

    timer = window.setTimeout(trigger, delay);
    if (scrollPercent > 0) window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  }

  /** Non copre il banner cookie di Shopify: aspetta che sia stato chiuso (massimo 60 secondi). */
  openWhenCookieBannerGone() {
    const started = Date.now();
    const check = () => {
      if (this.opened) return;
      const banner = document.getElementById('shopify-pc__banner');
      const visible = banner && banner.getClientRects().length > 0 && getComputedStyle(banner).visibility !== 'hidden';
      if (!visible) {
        this.open(false);
      } else if (Date.now() - started < 60000) {
        window.setTimeout(check, 500);
      }
    };
    check();
  }

  open(isSuccess) {
    if (this.opened || !this.dialog || this.dialog.open) return;
    this.opened = true;
    this.previousFocus = document.activeElement;
    this.dialog.showModal();
    // Il focus parte dal pannello (non dal pulsante di chiusura) per non mostrare un contorno subito
    if (isSuccess) this.successStep?.focus();
    else this.dialog.focus({ preventScroll: true });
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  rememberClosed() {
    if (this.subscribed) return;
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') this.previousFocus.focus();
    writeState({ status: 'closed', at: Date.now() });
  }

  showSuccess() {
    this.subscribed = true;
    writeState({ status: 'subscribed', at: Date.now() });
    if (this.formStep) this.formStep.hidden = true;
    if (this.successStep) this.successStep.hidden = false;
  }

  async submit(event) {
    if (!this.form.checkValidity()) return;
    if (!window.fetch) return; // invio nativo
    event.preventDefault();
    const button = this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    if (this.errorMessage) this.errorMessage.hidden = true;

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (response.url.includes('/challenge')) {
        // Shopify chiede la verifica anti-bot: si continua con l'invio normale
        this.form.submit();
        return;
      }
      if (response.ok && response.url.includes('customer_posted=true')) {
        this.showSuccess();
        this.successStep?.focus();
        return;
      }
      throw new Error('subscription failed');
    } catch (error) {
      if (this.errorMessage) this.errorMessage.hidden = false;
      if (button) button.disabled = false;
    }
  }

  async copyCode() {
    const code = this.querySelector('[data-popup-code]')?.textContent.trim();
    if (!code) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (error) {
      try {
        const range = document.createRange();
        range.selectNodeContents(this.querySelector('[data-popup-code]'));
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        copied = document.execCommand('copy');
      } catch (fallbackError) {
        copied = false;
      }
    }
    if (copied) {
      this.copyButton.textContent = this.copyButton.dataset.copied;
      if (this.status) this.status.textContent = this.copyButton.dataset.copied;
      window.setTimeout(() => {
        this.copyButton.textContent = this.copyButton.dataset.label;
      }, 2500);
    }
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
